import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';
import './AuthPages.css';

/* ─── EYE ICONS ───────────────────────────────────────────── */
const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const Register = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const { data } = await API.post('/auth/register', { name: form.name, email: form.email, phone: form.phone, password: form.password });
      toast.success(data.message || 'Verification code sent!');
      navigate('/verify-otp', { state: { email: data.email, name: form.name } });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: 'var(--bg3)', border: `1.5px solid var(--border)`, borderRadius: 10,
    color: 'var(--text)', padding: '12px 48px 12px 16px', fontSize: 15,
    fontFamily: "'Satoshi', 'DM Sans', sans-serif",
    transition: 'all 0.2s', width: '100%', boxSizing: 'border-box',
  };

  const plainInputStyle = {
    background: 'var(--bg3)', border: `1.5px solid var(--border)`, borderRadius: 10,
    color: 'var(--text)', padding: '12px 16px', fontSize: 15,
    fontFamily: "'Satoshi', 'DM Sans', sans-serif",
    transition: 'all 0.2s',
  };

  return (
    <main className="auth-page auth-page-register">
      <div className="auth-panel">
        <section className="auth-story">
          <div className="auth-story-content">
            <div className="auth-story-kicker">Start something great</div>
            <h1>Your next chapter<span>starts here.</span></h1>
            <p>Create your free account and take the next step toward a career in technology.</p>
            <div className="auth-story-points">
              <span><b aria-hidden="true">✓</b> Learn practical, industry-ready skills</span>
              <span><b aria-hidden="true">✓</b> Build projects and grow your portfolio</span>
              <span><b aria-hidden="true">✓</b> Earn verifiable certificates</span>
            </div>
          </div>
          <div className="auth-story-footer">Learn. Grow. Certify.</div>
        </section>

        <section className="auth-form-panel">
          <div className="auth-form-heading">
            <h2>Create your account</h2>
            <p>Join ThiranX and begin your learning journey.</p>
          </div>
          <form onSubmit={handleSubmit} className="auth-form-card">
            <div className="register-fields-grid">
              <div className="form-group">
                <label htmlFor="register-name">Full name</label>
                <input
                  id="register-name" name="name" value={form.name} onChange={handleChange}
                  placeholder="Your full name" autoComplete="name" required
                  style={plainInputStyle}
                />
              </div>

              <div className="form-group">
                <label htmlFor="register-email">Email address</label>
                <input
                  id="register-email" name="email" type="email" value={form.email} onChange={handleChange}
                  placeholder="you@example.com" autoComplete="email" required
                  style={plainInputStyle}
                />
              </div>

              <div className="form-group register-phone-field">
                <label htmlFor="register-phone">Phone number <span style={{ color: 'var(--muted)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  id="register-phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
                  placeholder="10-digit mobile number" autoComplete="tel"
                  style={plainInputStyle}
                />
              </div>
            </div>

            <div className="register-pw-grid">
              <div className="form-group">
                <label htmlFor="register-password">Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="register-password" name="password" type={showPassword ? 'text' : 'password'}
                    value={form.password} onChange={handleChange}
                    placeholder="Min 6 characters" autoComplete="new-password" required
                    style={inputStyle}
                  />
                  <button type="button" className="auth-password-toggle" onClick={() => setShowPassword(p => !p)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--muted)', padding: 0, display: 'flex', alignItems: 'center',
                  }}>
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="register-confirm-password">Confirm password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="register-confirm-password" name="confirmPassword" type={showConfirm ? 'text' : 'password'}
                    value={form.confirmPassword} onChange={handleChange}
                    placeholder="Repeat password" autoComplete="new-password" required
                    style={inputStyle}
                  />
                  <button type="button" className="auth-password-toggle" onClick={() => setShowConfirm(p => !p)} aria-label={showConfirm ? 'Hide password' : 'Show password'} style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--muted)', padding: 0, display: 'flex', alignItems: 'center',
                  }}>
                    {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary auth-submit">
              {loading ? 'Creating Account...' : 'Create Account & Start Learning'}
            </button>

            <p className="auth-switch">
              Already have an account?
              <Link to="/login">Login here</Link>
            </p>
          </form>
          <Link to="/" className="auth-home-link">← Back to Home</Link>
        </section>
      </div>
    </main>
  );
};

export default Register;
